import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpen, Timer, BarChart3, Users, CheckCircle2,
  Target, Sparkles, Brain, ClipboardList, TrendingUp, Flame, Award, Trophy, Crown,
  CalendarDays,
} from "lucide-react";
import TelegramIcon from "../components/icons/TelegramIcon";
import { SOCIAL_CHANNELS, openChannel } from "../lib/social";
import { Button } from "../components/ui/button";
import SiteFooter from "../components/SiteFooter";
import Logo from "../components/Logo";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5446a83f-0e04-4add-9d33-6b8e553aba77/images/342903b3536fcbc6f4266a438bad87e8d2a4a098473971c8cc8ef222c31ea9f3.png";
const STUDENT_IMG = "https://images.unsplash.com/photo-1758872014929-174e4ccdf01f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXRlJTIwbmlnaHQlMjBmb2N1c2VkfGVufDB8fHx8MTc3OTUzOTA1Mnww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: BookOpen, title: "Syllabus Tracker", desc: "Add subjects, chapters & track every milestone with auto progress bars." },
  { icon: Timer, title: "Study Stopwatch", desc: "Distraction-free timer with session history & auto-reset every midnight." },
  { icon: BarChart3, title: "Smart Reports", desc: "Daily, weekly & subject-wise analytics that show what's actually working." },
  { icon: Target, title: "Today's Target", desc: "Set 3 daily wins every morning, tick them off, and end the day proud." },
  { icon: CalendarDays, title: "Study Calendar", desc: "Plan your study schedule with a visual calendar, track daily consistency, upcoming goals, study sessions, and never miss your preparation routine." },
  { icon: Sparkles, title: "Syllox AI", desc: "Your 24/7 study companion — explanations, summaries & step-by-step help." },
  { icon: Brain, title: "AI Doubt Solving", desc: "Snap a question or type it — get clear, exam-ready answers instantly." },
  { icon: ClipboardList, title: "Mock Tests", desc: "Log every mock score, spot weak subjects, and watch your trend line climb." },
  { icon: TrendingUp, title: "Performance Analytics", desc: "Subject-wise insights, score prediction & consistency scoring built-in." },
  { icon: Flame, title: "Study Streaks", desc: "Show up daily. Your streak counts every minute and rewards consistency." },
  { icon: Award, title: "Achievements", desc: "Unlock badges for hours logged, streak milestones & chapter completions." },
  { icon: Trophy, title: "Leaderboards", desc: "Weekly, monthly & all-time rankings against fellow aspirants — friendly + fair." },
  { icon: Users, title: "Community (Pro)", desc: "Connect with fellow aspirants, share doubts, and grow together." },
];

// Hero left-column category pills — communicates who Syllox serves.
const HERO_CATEGORIES = [
  { emoji: "📚", label: "School Students" },
  { emoji: "🧪", label: "NEET" },
  { emoji: "⚙️", label: "JEE" },
  { emoji: "🎓", label: "College Students" },
  { emoji: "🏆", label: "All Competitive Exam Aspirants" },
];

// Hero right-column floating showcase cards. Static values only — these are
// illustrative, NOT pulled from any live user data or backend.
const HERO_SHOWCASE = [
  { emoji: "🔥", title: "27", subtitle: "Day Streak", pos: "left-0 top-2 -rotate-[5deg]", testid: "hero-showcase-streak" },
  { emoji: "🏆", title: "8", subtitle: "Achievements", pos: "right-0 top-10 rotate-[5deg]", testid: "hero-showcase-achievements" },
  { emoji: "🤖", title: "AI", subtitle: "Doubt Solving", pos: "left-1 bottom-20 -rotate-[4deg]", testid: "hero-showcase-ai" },
  { emoji: "📈", title: "+18%", subtitle: "Weekly Progress", pos: "right-1 bottom-4 rotate-[4deg]", testid: "hero-showcase-progress" },
];

// Mini streak heatmap values (last 14 days). Static — purely illustrative.
const HERO_HEATMAP = [3, 4, 2, 4, 5, 3, 4, 2, 4, 3, 5, 4, 3, 5];

function HeroShowcaseCard({ emoji, title, subtitle, pos, testid }) {
  return (
    <div
      data-testid={testid}
      className={`absolute ${pos} flex w-40 items-center gap-2.5 rounded-xl border border-zinc-200 bg-white/95 p-2.5 shadow-lg shadow-zinc-300/30 backdrop-blur dark:border-zinc-700 dark:bg-zinc-900/95 dark:shadow-black/40`}
    >
      <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>
      <div className="min-w-0 leading-tight">
        <div className="font-heading text-sm font-bold">{title}</div>
        <div className="truncate text-[10px] text-zinc-600 dark:text-zinc-400">{subtitle}</div>
      </div>
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="relative mx-auto aspect-[4/5] w-full max-w-md" data-testid="hero-visual">
      {/* Soft emerald backdrop — purely decorative, zero perf cost */}
      <div className="absolute inset-0 -z-10 rounded-[2rem] bg-gradient-to-br from-emerald-100/50 via-white/0 to-emerald-50/30 dark:from-emerald-500/15 dark:via-transparent dark:to-emerald-500/5" />
      <div className="absolute inset-6 -z-10 rounded-[2rem] bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.22),transparent_65%)] blur-2xl" />

      {/* Centerpiece — tilted "Live Session" preview card. Mimics the in-app dashboard so visitors immediately see what they're getting. */}
      <div className="absolute left-1/2 top-1/2 w-60 -translate-x-1/2 -translate-y-1/2 -rotate-[3deg] rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl shadow-emerald-500/10 dark:border-zinc-800 dark:bg-zinc-900" data-testid="hero-centerpiece">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Live Session</span>
        </div>
        <div className="mt-3 font-heading text-3xl font-bold leading-none">2h 45m</div>
        <div className="mt-1 text-xs text-zinc-500">Physics · Thermodynamics</div>

        <div className="mt-4">
          <div className="mb-1.5 text-[10px] uppercase tracking-wider text-zinc-500">Streak · last 14 days</div>
          <div className="flex gap-[3px]">
            {HERO_HEATMAP.map((v, i) => (
              <div key={`hm-${i}`} className="h-3 flex-1 rounded-[2px] bg-emerald-500" style={{ opacity: v / 5 }} />
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-1.5">
          <div className="flex justify-between text-[10px] text-zinc-500"><span>Physics</span><span>72%</span></div>
          <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-800"><div className="h-full w-[72%] rounded-full bg-emerald-500" /></div>
          <div className="flex justify-between text-[10px] text-zinc-500"><span>Chemistry</span><span>54%</span></div>
          <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-800"><div className="h-full w-[54%] rounded-full bg-emerald-400" /></div>
        </div>
      </div>

      {/* 4 floating showcase cards positioned around the centerpiece */}
      {HERO_SHOWCASE.map((c) => (
        <HeroShowcaseCard key={c.testid} {...c} />
      ))}
    </div>
  );
}

export default function Landing() {
  useEffect(() => {
    document.title = "Syllox — Built for Aspirants | NEET & JEE Study Tracker";
  }, []);
  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-zinc-200/50 bg-white/70 backdrop-blur-xl dark:border-zinc-800/50 dark:bg-zinc-950/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Logo className="h-9 w-9" />
            <div>
              <div className="font-heading text-lg font-bold leading-none">Syllox</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Built for Aspirants</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost">Login</Button>
            </Link>
            <Link to="/auth/phone" data-testid="nav-signup-link">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">Get Started <ArrowRight size={16} className="ml-1" /></Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-30 dark:opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-zinc-50/60 to-zinc-50 dark:via-zinc-950/60 dark:to-zinc-950" />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
            <div className="max-w-2xl">
              <div className="flex flex-wrap gap-2" data-testid="hero-category-badges">
                {HERO_CATEGORIES.map((c) => (
                  <span
                    key={c.label}
                    data-testid={`hero-category-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400"
                  >
                    <span aria-hidden="true">{c.emoji}</span>
                    {c.label}
                  </span>
                ))}
              </div>
              <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tighter sm:text-5xl lg:text-7xl" data-testid="hero-title">
                Built for Aspirants.<br />
                <span className="text-emerald-500">Engineered for focus.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 dark:text-zinc-400 sm:text-lg">
                Track your syllabus, log every study hour, and turn consistency into your superpower. The all-in-one productivity OS for serious students.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/auth/phone" data-testid="hero-cta-signup">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 transition-transform hover:-translate-y-0.5">
                    Start free <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-cta-login">
                  <Button size="lg" variant="outline">I have an account</Button>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                {["No credit card required", "Streak tracking", "Private accounts"].map((t) => (
                  <div key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-500" />{t}</div>
                ))}
              </div>
            </div>

            {/* Right-side visual — hidden on mobile to keep the page lean.
                Static composition: 4 floating showcase cards around a tilted live-session preview. No images, no animations beyond the existing pulse dot, no API calls. */}
            <div className="hidden lg:block">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="features-section">
        <div className="mb-12 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">Features</div>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Everything you need to crack it.</h2>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 sm:text-base">
            Syllabus tracking, AI assistance, doubt solving, mock tests, analytics, targets, streaks, achievements, leaderboards, community — all in one place.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className="flex h-full flex-col rounded-2xl border border-zinc-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-emerald-500/40 hover:shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
            >
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <f.icon size={20} />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{f.desc}</p>
            </div>
          ))}

          {/* Premium Features — full-width highlight card */}
          <div
            data-testid="feature-card-premium-features"
            className="flex h-full flex-col rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-6 transition-all hover:-translate-y-1 hover:shadow-lg dark:border-emerald-500/30 dark:from-emerald-500/10 dark:via-zinc-900 dark:to-emerald-500/5 sm:col-span-2 lg:col-span-3 xl:col-span-4 sm:flex-row sm:items-center sm:gap-6"
          >
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber-500 text-white shadow-sm shadow-amber-500/30">
              <Crown size={20} />
            </div>
            <div className="mt-4 sm:mt-0 sm:flex-1">
              <h3 className="font-heading text-lg font-semibold">Premium Features</h3>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                Unlock unlimited chapters, AI doubt solving, the full Study Calendar, premium themes, community, friends, private messaging, and advanced reports — built for serious aspirants.
              </p>
            </div>
            <Link to="/premium" data-testid="features-premium-cta" className="mt-4 sm:mt-0 sm:shrink-0">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                See Premium <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote / Image */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 overflow-hidden rounded-3xl border border-zinc-200 bg-white md:grid-cols-2 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="p-8 md:p-12">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Made for late-night warriors</div>
            <h3 className="mt-3 font-heading text-2xl font-semibold sm:text-3xl">Your discipline, visualized.</h3>
            <p className="mt-4 text-zinc-600 dark:text-zinc-400">Every minute you log compounds into a story — streaks, completion %, and weekly heatmaps that prove how far you&apos;ve come.</p>
            <Link to="/auth/phone" className="mt-6 inline-block" data-testid="bottom-cta-signup">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">Create your account</Button>
            </Link>
          </div>
          <div className="relative min-h-[260px]">
            <img src={STUDENT_IMG} alt="focused student" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Official Telegram Channel — free study resources funnel. Sits just
          above the footer so it's the last thing a visitor sees before the
          navigation region. Read-only content; click routes users off-site. */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="landing-telegram-section">
        <div className="relative overflow-hidden rounded-3xl border border-sky-500/25 bg-gradient-to-br from-sky-500/10 via-white to-white p-8 md:p-12 dark:border-sky-500/25 dark:from-sky-500/10 dark:via-zinc-900 dark:to-zinc-900">
          {/* Ambient background glow — CSS-only, no motion listeners */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-sky-400/20 blur-3xl" aria-hidden="true" />

          <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-[auto,1fr,auto]">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-xl shadow-sky-500/25">
              <TelegramIcon size={32} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sky-600 dark:text-sky-400">Official channel · {SOCIAL_CHANNELS.telegram.handle}</div>
              <h3 className="mt-1 font-heading text-2xl font-semibold sm:text-3xl">
                Join the Official Syllox Telegram Channel
              </h3>
              <p className="mt-3 max-w-2xl text-zinc-600 dark:text-zinc-400">
                {SOCIAL_CHANNELS.telegram.landingDescription}
              </p>
            </div>
            <button
              type="button"
              onClick={() => openChannel("telegram")}
              data-testid="landing-telegram-join"
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-5 py-3 font-medium text-white shadow-lg shadow-sky-500/25 transition hover:bg-sky-600 hover:shadow-xl active:scale-[0.98]"
            >
              <TelegramIcon size={18} />
              Join Telegram
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
